import { ArrayUtil } from "../ArrayUtil";
import { NumberUtil } from "../NumberUtil";
import { Unit } from "./Unit";
import { UnitConversionFactor } from "./UnitConversionFactor";
import { UnitConversionFactorGroup } from "./UnitConversionFactorGroup";
import { UnitGroup } from "./UnitGroup";

export type NumberWithUnitSource = number | NumberWithUnit | NumberWithUnitProps;
export type UnitSource = Unit | string;

export interface NumberWithUnitProps {
    number?: number | string;
    unit?: UnitSource;
    functionalNumber?: number;
    functionalUnit?: UnitSource;
}

export abstract class NumberWithUnit {
    private _number: number;
    private _unit: Unit;
    private _functionalNumber?: number;
    private _functionalUnit?: Unit;
    public searchUnits: UnitSource[];

    constructor(source: NumberWithUnitSource) {
        if (typeof source === "number" || typeof source === "string") {
            this.number = NumberUtil.getFloat(source);
        } else if (source instanceof NumberWithUnit) {
            this.unit = source.unit ?? this.companyDefaultUnit;
            this.functionalUnit = source.functionalUnit ?? this.getCompanyFunctionalUnit();
            this.number = source.number != null ? Number(source.number) : 0;
            if (source.functionalNumber != null) {
                this.functionalNumber = Number(source.functionalNumber);
            }
            
        } else {
            this.unit = this.resolveUnit(source?.unit) ?? this.companyDefaultUnit;
            this.functionalUnit = source?.functionalUnit ?? this.getCompanyFunctionalUnit();
            this.number = source?.number != null ? NumberUtil.getFloat(source.number) : 0;
            if (source.functionalNumber != null) {
                this.functionalNumber = Number(source.functionalNumber);
            }
        }
    }

    public get number(): number {
        return this._number;
    }

    public set number(value: number) {
        this._number = value;
        this.resetFunctionalNumber();
    }

    public get unit(): Unit {
        return this._unit;
    }

    public set unit(unit: UnitSource) {
        this._unit = this.resolveUnit(unit);
        this.resetFunctionalNumber();
    }

    public get functionalNumber(): number {
        return this._functionalNumber;
    }

    public set functionalNumber(value: number) {
        this._functionalNumber = value;
    }

    private resetFunctionalNumber() {
        this.functionalNumber = this.getNumberIn(this.functionalUnit);
    }

    public get functionalUnit(): Unit {
        return this._functionalUnit;
    }

    public set functionalUnit(unit: UnitSource) {
        this._functionalUnit = this.resolveUnit(unit);
    }

    private resolveUnit(unit: UnitSource): Unit {
        if (typeof unit === "string") {
            return this.unitGroup.unitsByAbbreviation.get(unit);
        }
        return unit;
    }

    public get companyDefaultUnit(): Unit {
        return this.unitGroup.companyDefaultUnit;
    }

    public getCompanyFunctionalUnit(): Unit {
        return this.unitGroup.companyFunctionalUnit;
    }

    public format(format?: string): string {
        return NumberUtil.formatDecimal(this.number, format);
    }

    public getUnformattedNumber(text: string = String(this.number)): string {
        return text.replace(/[^\d\.\-]/g, "");
    }

    /**
     * Gets the number, optionally converted to the specified target unit.
     */
    public getNumberIn(unit: Unit): number {
        if (unit == null || unit === this.unit) {
            return this.number;
        }
        const result = this.convert(unit);
        return Math.round(result * 10) / 10;
    }

    /** Returns a shallow clone with the same quantity, optionally converting to a target unit. */
    public getIn(unit: Unit): NumberWithUnit {
        return new this.cls({ number: this.getNumberIn(unit), unit });
    }

    public abstract convert(targetUnit: UnitSource): number;

    public convertWithPrecision(targetUnit: string, precision: number): number {
        if (this.unit == null || this.unit.abbreviation === targetUnit) {
            return this.number;
        }

        const conversionFactor = this.getConversionFactor(targetUnit);
        if (conversionFactor == null) {
            const errorMessage = `Unable to convert value ${this.number} into unit ${targetUnit}, unit not supported`;
            throw new Error(errorMessage);
        }

        const conversionFactorValue = conversionFactor.factor;
        return NumberUtil.round(precision, this.number * conversionFactorValue);
    }

    public getConversionFactor(targetUnit: UnitSource): UnitConversionFactor {
        const targetUnitAbbreviation = targetUnit instanceof Unit ? targetUnit.abbreviation : targetUnit;
        return this.getConversionFactors().getConversionFactor(this.unit, targetUnitAbbreviation);
    }

    public abstract getConversionFactors(): UnitConversionFactorGroup;

    protected abstract get cls(): NumberWithUnitConstructor;
    public abstract get unitGroup(): UnitGroup;
    
    public equals(otherNumberWithUnit: NumberWithUnit): boolean {
        if (otherNumberWithUnit == null) {
            return false;
        }
        const otherNumberConverted = otherNumberWithUnit.getNumberIn(this.unit);
        return this.number === otherNumberConverted;
    }

    /**
     * Implement toJSON so that the info is serialized in the desired format.
     * @returns any
     */
    public toJSON(): any {
        return {
            number: this.number,
            unit: this.getUnitValueForServer(),
            func_number: this.functionalNumber,
            func_unit: this.functionalUnit?.abbreviation
        };
    }

    /**
     * This method allows the json body sent to the server to contain the correct representation of units when
     * searching.  Since a NumberWithUnit object's unit value would normally be a single unit, the separate searchUnits
     * object is used to hold the multiple units that might be included in search criteria.
     */
    private getUnitValueForServer() {
        if (!ArrayUtil.isEmptyArray(this.searchUnits)) {
            if (this.searchUnits.length < 2) {
                return Unit.resolveAbbreviation(ArrayUtil.getFirstElement(this.searchUnits));
            }
            return "in " + this.searchUnits.map(unit => Unit.resolveAbbreviation(unit)).join(",");
        }
        return this.unit?.abbreviation;
    }

    updateForSearch(userSuppliedNumber: boolean) {
        if (!userSuppliedNumber) {
            this._number = undefined;
        }
        this._functionalNumber = undefined;
        this._functionalUnit = undefined;
    }
}

export type NumberWithUnitConstructor<T extends NumberWithUnit = NumberWithUnit> = new (...args: any[]) => T;
